// ============================================================
// PALETTE SCENE
// Overlay showing biome palette entries — each biome's resources:
// biome (ma X), stone (kiwen X), plant (kasi X), and 5 creature
// categories (soweli/akesi/kala/waso/pipi X) with their toki pona
// glyph pairs and sprites where available.
//
// When a player position is provided, the current biome is shown
// prominently at the top with full display, and the scrollable
// list auto-scrolls to its zone.
//
// Launched from GameScene via P key.
// ============================================================

import Phaser from 'phaser';
import {
    BIOME_PALETTES,
    BIOME_ZONE_NAMES,
    BIOME_ZONE_GLYPHS,
    BiomeZone,
    PALETTE_WORD_MA,
    PALETTE_WORD_KIWEN,
    PALETTE_WORD_KASI,
    CREATURE_CATEGORIES,
    CREATURE_CATEGORY_WORD_INDEX,
    getSurfaceBiomeAtX,
    getBiomePaletteIndex,
    getBiomeName,
} from '@blockworld/shared';
import {
    GLYPH_SIZE,
    getGlyphColor,
    getWord,
    getGlyphIndex,
    SPECIAL_GLYPHS,
} from '../toki/TokiPonaData';
import { createPixelFont, PIXEL_FONT_KEY } from '../rendering/PixelFont';
import { SPRITE_MAP, DEFINITION_SPRITE_MAP } from './DictionaryScene';

// Layout constants
const PADDING = 4;
const HEADER_HEIGHT = 12;        // top header area with title + ESC
const ROW_HEIGHT = 10;           // per-entry row height
const ZONE_HEADER_HEIGHT = 12;   // zone section header height
const GLYPH_DRAW = 8;            // glyph draw size (native 8x8)
const SPRITE_DRAW = 8;           // sprite draw size
const COL_GAP = 3;               // gap between triad columns
const PAIR_GAP = 1;              // gap between two glyphs in a pair
const SCROLL_SPEED = 2;          // rows per scroll step

// Featured biome section sizes
const FEATURED_GLYPH = 8;       // glyph size (native 8x8, no scaling)
const FEATURED_SPRITE = 8;      // sprite size (native 8x8)
const FEATURED_ROW_H = 10;      // row height in featured section
const FEATURED_SEPARATOR = 2;   // separator below featured section

// Column layout: [biome pair] [stone sprite + pair] [plant sprite + pair] [5 creature sprites]
// Each pair = 2 glyphs = 16px + PAIR_GAP, sprite = 8px
const BIOME_COL_W = GLYPH_DRAW * 2 + PAIR_GAP;  // 17px
const STONE_COL_W = SPRITE_DRAW + 2 + GLYPH_DRAW * 2 + PAIR_GAP;  // 27px
const PLANT_COL_W = SPRITE_DRAW + 2 + GLYPH_DRAW * 2 + PAIR_GAP;  // 27px
const CREATURE_COL_W = SPRITE_DRAW * 5 + 4;  // 5 sprites (8px each) + 4×1px gaps = 44px
const TOTAL_ROW_W = BIOME_COL_W + COL_GAP + STONE_COL_W + COL_GAP + PLANT_COL_W + COL_GAP + CREATURE_COL_W; // 124px

export class PaletteScene extends Phaser.Scene {
    private onClose: (() => void) | null = null;
    private onOpenDictionary: ((wordA: number, wordB: number) => void) | null = null;

    // Current biome detection
    private currentBiomeWord: number = -1;
    private featuredHeight: number = 0;

    // Scrollable content container
    private contentContainer!: Phaser.GameObjects.Container;
    private scrollY: number = 0;
    private maxScrollY: number = 0;
    private visibleHeight: number = 0;

    // Scroll indicator
    private scrollBar!: Phaser.GameObjects.Rectangle;
    private scrollTrack!: Phaser.GameObjects.Rectangle;

    // Track row Y positions for auto-scroll
    private biomeRowYPositions: Map<number, number> = new Map();

    constructor() {
        super({ key: 'PaletteScene' });
    }

    init(data: {
        onClose?: () => void;
        onOpenDictionary?: (wordA: number, wordB: number) => void;
        playerTileX?: number;
    }): void {
        this.onClose = data.onClose || null;
        this.onOpenDictionary = data.onOpenDictionary || null;
        this.currentBiomeWord = data.playerTileX !== undefined
            ? getSurfaceBiomeAtX(data.playerTileX)
            : -1;
    }

    create(): void {
        const { width, height } = this.cameras.main;

        createPixelFont(this);

        this.scrollY = 0;
        this.biomeRowYPositions.clear();

        // Semi-transparent overlay background
        const overlay = this.add.rectangle(0, 0, width, height, 0x000000, 0.85);
        overlay.setOrigin(0, 0).setDepth(0);

        // ── Header ──
        const titleY = PADDING;
        let tx = PADDING;

        // Title: "ma" + "kiwen" + "kasi" + creature category glyphs
        const headerGlyphs = [
            PALETTE_WORD_MA,
            PALETTE_WORD_KIWEN,
            PALETTE_WORD_KASI,
            ...CREATURE_CATEGORIES.map(c => CREATURE_CATEGORY_WORD_INDEX[c]),
        ];
        for (const glyphIdx of headerGlyphs) {
            const g = this.add.image(tx, titleY, 'toki_glyphs', glyphIdx);
            g.setOrigin(0, 0).setDisplaySize(GLYPH_DRAW, GLYPH_DRAW).setDepth(10);
            g.setTint(getGlyphColor(glyphIdx));
            tx += GLYPH_DRAW + 2;
        }

        // ESC button (top-right)
        const alaIdx = getGlyphIndex(SPECIAL_GLYPHS.ESCAPE);
        const escBtn = this.add.image(width - PADDING - GLYPH_DRAW, titleY, 'toki_glyphs', alaIdx);
        escBtn.setOrigin(0, 0).setDisplaySize(GLYPH_DRAW, GLYPH_DRAW).setDepth(10);
        escBtn.setTint(getGlyphColor(alaIdx));
        escBtn.setInteractive({ useHandCursor: true });
        escBtn.on('pointerdown', () => this.closeScene());

        // ── Keys ──
        const escKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.ESC);
        escKey.on('down', () => this.closeScene());
        const pKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.P);
        pKey.on('down', () => this.closeScene());

        // ── Featured biome section (if current biome is known) ──
        this.featuredHeight = 0;
        if (this.currentBiomeWord >= 0) {
            this.featuredHeight = this.buildFeaturedSection(HEADER_HEIGHT, width);
        }

        // ── Scrollable content ──
        const contentTop = HEADER_HEIGHT + this.featuredHeight;
        this.visibleHeight = height - contentTop - PADDING;

        // Mask for scrollable area
        const maskShape = this.make.graphics({ x: 0, y: 0 }, false);
        maskShape.fillRect(0, contentTop, width, this.visibleHeight);
        const mask = maskShape.createGeometryMask();

        this.contentContainer = this.add.container(0, contentTop);
        this.contentContainer.setDepth(5);
        this.contentContainer.setMask(mask);

        // Build all rows
        const contentStartX = Math.floor((width - TOTAL_ROW_W) / 2);
        let cy = 0; // cursor Y within content container
        let currentZone: BiomeZone | null = null;

        for (let i = 0; i < BIOME_PALETTES.length; i++) {
            const entry = BIOME_PALETTES[i];

            // Zone header when zone changes
            if (entry.zone !== currentZone) {
                currentZone = entry.zone;
                cy = this.addZoneHeader(cy, contentStartX, entry.zone);
            }

            // Track row position for auto-scroll
            this.biomeRowYPositions.set(entry.word, cy);

            // Entry row — highlight current biome
            const isCurrentBiome = entry.word === this.currentBiomeWord;
            if (isCurrentBiome) {
                const highlight = this.add.rectangle(
                    0, cy, width, ROW_HEIGHT, 0x446644, 0.4
                );
                highlight.setOrigin(0, 0).setDepth(5);
                this.contentContainer.add(highlight);
            }

            this.addEntryRow(cy, contentStartX, entry.word);
            cy += ROW_HEIGHT;
        }

        this.maxScrollY = Math.max(0, cy - this.visibleHeight);

        // ── Scroll track + thumb ──
        const trackX = width - 3;
        const trackH = this.visibleHeight;
        this.scrollTrack = this.add.rectangle(trackX, contentTop, 2, trackH, 0x222222);
        this.scrollTrack.setOrigin(0, 0).setDepth(15);

        const thumbH = Math.max(8, Math.floor(trackH * (this.visibleHeight / (cy || 1))));
        this.scrollBar = this.add.rectangle(trackX, contentTop, 2, thumbH, 0x666666);
        this.scrollBar.setOrigin(0, 0).setDepth(16);

        // ── Scroll input ──
        this.input.on('wheel', (_p: Phaser.Input.Pointer, _over: any[], _dx: number, dy: number) => {
            this.scroll(dy > 0 ? SCROLL_SPEED : -SCROLL_SPEED);
        });

        const upKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.UP);
        const downKey = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.DOWN);
        upKey.on('down', () => this.scroll(-SCROLL_SPEED));
        downKey.on('down', () => this.scroll(SCROLL_SPEED));

        // ── Auto-scroll to current biome ──
        if (this.currentBiomeWord >= 0) {
            const targetY = this.biomeRowYPositions.get(this.currentBiomeWord);
            if (targetY !== undefined) {
                // Scroll so the current biome row is near the top of the visible area
                this.scrollY = Phaser.Math.Clamp(
                    targetY - ROW_HEIGHT * 2,  // a couple rows of margin
                    0,
                    this.maxScrollY,
                );
            }
        }

        this.updateScroll();
    }

    /**
     * Build the featured biome section at the top showing the current
     * biome's full palette prominently.
     * Returns the total height used by this section.
     */
    private buildFeaturedSection(startY: number, sceneWidth: number): number {
        const wordIdx = this.currentBiomeWord;
        if (wordIdx < 0) return 0;

        let y = startY;

        // Background highlight for featured section
        const featuredBg = this.add.rectangle(
            0, y, sceneWidth, 0, 0x224422, 0.6
        );
        featuredBg.setOrigin(0, 0).setDepth(1);

        // Row 1: English biome name, centered
        const biomeName = getBiomeName(wordIdx);
        if (biomeName) {
            const nameLabel = this.add.bitmapText(0, y + 1, PIXEL_FONT_KEY, biomeName);
            nameLabel.setOrigin(0, 0).setDepth(10);
            nameLabel.setTint(0xaaccaa);
            // Center after creation (bitmapText width only known after adding)
            nameLabel.x = Math.floor((sceneWidth - nameLabel.width) / 2);
        }
        y += FEATURED_ROW_H;

        // Row 2: All palette items in one row — biome, stone, plant, 5 creatures
        // Build list of items: [categoryWordIndex, wordIndex, spriteKey|null]
        const items: Array<{ catWord: number; word: number; spriteKey: string | null }> = [];

        // Biome (ma)
        items.push({ catWord: PALETTE_WORD_MA, word: wordIdx, spriteKey: this.resolveTexture(PALETTE_WORD_MA, wordIdx) });
        // Stone (kiwen)
        items.push({ catWord: PALETTE_WORD_KIWEN, word: wordIdx, spriteKey: this.resolveTexture(PALETTE_WORD_KIWEN, wordIdx) });
        // Plant (kasi)
        items.push({ catWord: PALETTE_WORD_KASI, word: wordIdx, spriteKey: this.resolveTexture(PALETTE_WORD_KASI, wordIdx) });
        // 5 creature categories
        for (const cat of CREATURE_CATEGORIES) {
            const catWordIdx = CREATURE_CATEGORY_WORD_INDEX[cat];
            const creatureKey = `rodent_${cat}_${wordIdx}`;
            items.push({
                catWord: catWordIdx,
                word: wordIdx,
                spriteKey: this.textures.exists(creatureKey) ? creatureKey : null,
            });
        }

        // Layout: each item is FEATURED_SPRITE wide, with PAIR_GAP between
        const itemW = FEATURED_SPRITE;
        const itemGap = 2;
        const totalW = items.length * itemW + (items.length - 1) * itemGap;
        let rx = Math.floor((sceneWidth - totalW) / 2);

        for (const item of items) {
            // Draw sprite if available, otherwise category glyph as fallback
            if (item.spriteKey) {
                const spr = this.add.image(rx, y + 1, item.spriteKey);
                spr.setOrigin(0, 0).setDisplaySize(FEATURED_SPRITE, FEATURED_SPRITE).setDepth(10);
            } else {
                const glyph = this.add.image(rx, y + 1, 'toki_glyphs', item.catWord);
                glyph.setOrigin(0, 0).setDisplaySize(FEATURED_GLYPH, FEATURED_GLYPH).setDepth(10);
                glyph.setTint(getGlyphColor(item.catWord));
            }

            // Hit zone with hover feedback (same style as scrollable list)
            this.addFeaturedHitZone(rx, y, itemW, FEATURED_ROW_H, item.catWord, item.word);
            rx += itemW + itemGap;
        }

        y += FEATURED_ROW_H;

        // Separator line
        const sep = this.add.rectangle(0, y, sceneWidth, FEATURED_SEPARATOR, 0x446644);
        sep.setOrigin(0, 0).setDepth(10);
        y += FEATURED_SEPARATOR;

        // Resize featured background to actual height
        const totalHeight = y - startY;
        featuredBg.height = totalHeight;

        return totalHeight;
    }

    /** Add a clickable hit zone in the featured section with hover highlight. */
    private addFeaturedHitZone(x: number, y: number, w: number, h: number, wordA: number, wordB: number): void {
        const zone = this.add.rectangle(x, y, w, h, 0xffffff, 0);
        zone.setOrigin(0, 0).setDepth(11);
        zone.setInteractive({ useHandCursor: true });
        zone.on('pointerover', () => zone.setFillStyle(0xffffff, 0.15));
        zone.on('pointerout', () => zone.setFillStyle(0xffffff, 0));
        zone.on('pointerdown', () => {
            if (this.onOpenDictionary) {
                this.onOpenDictionary(wordA, wordB);
                this.closeScene();
            }
        });
    }

    /** Add a zone section header row. Returns new Y position. */
    private addZoneHeader(y: number, x: number, zone: BiomeZone): number {
        const glyphs = BIOME_ZONE_GLYPHS[zone];
        const name = BIOME_ZONE_NAMES[zone];

        // Zone glyph pair
        const g1 = this.add.image(x, y + 2, 'toki_glyphs', glyphs[0]);
        g1.setOrigin(0, 0).setDisplaySize(GLYPH_DRAW, GLYPH_DRAW).setDepth(6);
        g1.setTint(getGlyphColor(glyphs[0]));
        this.contentContainer.add(g1);

        const g2 = this.add.image(x + GLYPH_DRAW + PAIR_GAP, y + 2, 'toki_glyphs', glyphs[1]);
        g2.setOrigin(0, 0).setDisplaySize(GLYPH_DRAW, GLYPH_DRAW).setDepth(6);
        g2.setTint(getGlyphColor(glyphs[1]));
        this.contentContainer.add(g2);

        // Zone name text
        const label = this.add.bitmapText(
            x + GLYPH_DRAW * 2 + PAIR_GAP + 4, y + 2,
            PIXEL_FONT_KEY, name
        );
        label.setOrigin(0, 0).setDepth(6);
        label.setTint(0x888888);
        this.contentContainer.add(label);

        // Separator line
        const line = this.add.rectangle(x, y + ZONE_HEADER_HEIGHT - 1, TOTAL_ROW_W, 1, 0x333333);
        line.setOrigin(0, 0).setDepth(6);
        this.contentContainer.add(line);

        return y + ZONE_HEADER_HEIGHT;
    }

    /** Add a single palette entry row: biome glyphs, stone (sprite+glyphs), plant (sprite+glyphs). */
    private addEntryRow(y: number, startX: number, wordIdx: number): void {
        let x = startX;
        const glyphY = y + 1; // vertically center in ROW_HEIGHT

        // ── Biome column: [ma][word] ──
        this.addGlyphPair(x, glyphY, PALETTE_WORD_MA, wordIdx);
        this.addColumnHitZone(x, y, BIOME_COL_W, ROW_HEIGHT, PALETTE_WORD_MA, wordIdx);
        x += BIOME_COL_W + COL_GAP;

        // ── Stone column: [sprite] [kiwen][word] ──
        const stoneColX = x;
        const stoneSpriteKey = this.resolveTexture(PALETTE_WORD_KIWEN, wordIdx);
        if (stoneSpriteKey) {
            const sprite = this.add.image(x, glyphY, stoneSpriteKey);
            sprite.setOrigin(0, 0).setDisplaySize(SPRITE_DRAW, SPRITE_DRAW).setDepth(6);
            this.contentContainer.add(sprite);
            x += SPRITE_DRAW + 2;
        }
        this.addGlyphPair(x, glyphY, PALETTE_WORD_KIWEN, wordIdx);
        this.addColumnHitZone(stoneColX, y, STONE_COL_W, ROW_HEIGHT, PALETTE_WORD_KIWEN, wordIdx);
        x = startX + BIOME_COL_W + COL_GAP + STONE_COL_W + COL_GAP;

        // ── Plant column: [sprite] [kasi][word] ──
        const plantColX = x;
        const plantSpriteKey = this.resolveTexture(PALETTE_WORD_KASI, wordIdx);
        if (plantSpriteKey) {
            const sprite = this.add.image(x, glyphY, plantSpriteKey);
            sprite.setOrigin(0, 0).setDisplaySize(SPRITE_DRAW, SPRITE_DRAW).setDepth(6);
            this.contentContainer.add(sprite);
            x += SPRITE_DRAW + 2;
        }
        this.addGlyphPair(x, glyphY, PALETTE_WORD_KASI, wordIdx);
        this.addColumnHitZone(plantColX, y, PLANT_COL_W, ROW_HEIGHT, PALETTE_WORD_KASI, wordIdx);
        x = startX + BIOME_COL_W + COL_GAP + STONE_COL_W + COL_GAP + PLANT_COL_W + COL_GAP;

        // ── Creature columns: 5 sprites (soweli, akesi, kala, waso, pipi) ──
        for (const cat of CREATURE_CATEGORIES) {
            const catWordIdx = CREATURE_CATEGORY_WORD_INDEX[cat];
            const creatureSpriteKey = `rodent_${cat}_${wordIdx}`;
            if (this.textures.exists(creatureSpriteKey)) {
                const sprite = this.add.image(x, glyphY, creatureSpriteKey);
                sprite.setOrigin(0, 0).setDisplaySize(SPRITE_DRAW, SPRITE_DRAW).setDepth(6);
                this.contentContainer.add(sprite);
            }
            // Hit zone for each creature sprite
            const hitZone = this.add.rectangle(x, y, SPRITE_DRAW, ROW_HEIGHT, 0xffffff, 0);
            hitZone.setOrigin(0, 0).setDepth(7);
            hitZone.setInteractive({ useHandCursor: true });
            hitZone.on('pointerover', () => hitZone.setFillStyle(0xffffff, 0.15));
            hitZone.on('pointerout', () => hitZone.setFillStyle(0xffffff, 0));
            hitZone.on('pointerdown', () => {
                if (this.onOpenDictionary) {
                    this.onOpenDictionary(catWordIdx, wordIdx);
                    this.closeScene();
                }
            });
            this.contentContainer.add(hitZone);
            x += SPRITE_DRAW + 1;
        }
    }

    /** Add an invisible clickable hit zone for a column that navigates to the dictionary. */
    private addColumnHitZone(x: number, y: number, w: number, h: number, wordA: number, wordB: number): void {
        const zone = this.add.rectangle(x, y, w, h, 0xffffff, 0);
        zone.setOrigin(0, 0).setDepth(7);
        zone.setInteractive({ useHandCursor: true });
        zone.on('pointerover', () => zone.setFillStyle(0xffffff, 0.15));
        zone.on('pointerout', () => zone.setFillStyle(0xffffff, 0));
        zone.on('pointerdown', () => {
            if (this.onOpenDictionary) {
                this.onOpenDictionary(wordA, wordB);
                this.closeScene();
            }
        });
        this.contentContainer.add(zone);
    }

    /** Render a 2-glyph toki pona word pair (e.g. ma + ala). */
    private addGlyphPair(x: number, y: number, word1: number, word2: number): void {
        const g1 = this.add.image(x, y, 'toki_glyphs', word1);
        g1.setOrigin(0, 0).setDisplaySize(GLYPH_DRAW, GLYPH_DRAW).setDepth(6);
        g1.setTint(getGlyphColor(word1));
        this.contentContainer.add(g1);

        const g2 = this.add.image(x + GLYPH_DRAW + PAIR_GAP, y, 'toki_glyphs', word2);
        g2.setOrigin(0, 0).setDisplaySize(GLYPH_DRAW, GLYPH_DRAW).setDepth(6);
        g2.setTint(getGlyphColor(word2));
        this.contentContainer.add(g2);
    }

    /** Resolve a texture key for a word pair, reusing DictionaryScene / SpriteBrowserScene logic. */
    private resolveTexture(indexA: number, indexB: number): string | null {
        const wordA = getWord(indexA);
        const wordB = getWord(indexB);
        if (!wordA || !wordB) return null;

        const key = `${wordA} ${wordB}`;

        // Check SPRITE_MAP
        let texKey = SPRITE_MAP[key];
        if (texKey && this.textures.exists(texKey)) return texKey;

        // Check dynamic tile_* keys (loaded for placeholder tiles)
        const underscored = key.replace(/ /g, '_');
        const tileKey = `tile_${underscored}`;
        if (this.textures.exists(tileKey)) return this.ensureTransparent(tileKey);

        // Check processed placeholder textures
        const placeholderKey = `placeholder_${underscored}`;
        if (this.textures.exists(placeholderKey)) return placeholderKey;

        return null;
    }

    /** Make black pixels transparent for a raw texture, caching the result. */
    private ensureTransparent(rawKey: string): string {
        const processedKey = rawKey + '_palette_processed';
        if (this.textures.exists(processedKey)) return processedKey;

        const texture = this.textures.get(rawKey);
        if (!texture) return rawKey;
        const source = texture.getSourceImage() as HTMLImageElement;

        const canvas = document.createElement('canvas');
        canvas.width = source.width;
        canvas.height = source.height;
        const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
        ctx.drawImage(source, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        const threshold = 10;
        for (let i = 0; i < data.length; i += 4) {
            if (data[i] <= threshold && data[i + 1] <= threshold && data[i + 2] <= threshold) {
                data[i + 3] = 0;
            }
        }
        ctx.putImageData(imageData, 0, 0);
        this.textures.addCanvas(processedKey, canvas);
        return processedKey;
    }

    /** Scroll the content container by a number of row-height increments. */
    private scroll(rows: number): void {
        this.scrollY = Phaser.Math.Clamp(
            this.scrollY + rows * ROW_HEIGHT,
            0,
            this.maxScrollY,
        );
        this.updateScroll();
    }

    /** Apply current scrollY to the content container and scroll indicator. */
    private updateScroll(): void {
        const contentTop = HEADER_HEIGHT + this.featuredHeight;
        this.contentContainer.y = contentTop - this.scrollY;

        // Update scroll bar position
        if (this.maxScrollY > 0) {
            const ratio = this.scrollY / this.maxScrollY;
            const trackH = this.visibleHeight - this.scrollBar.height;
            this.scrollBar.y = contentTop + ratio * trackH;
            this.scrollBar.setVisible(true);
            this.scrollTrack.setVisible(true);
        } else {
            this.scrollBar.setVisible(false);
            this.scrollTrack.setVisible(false);
        }
    }

    private closeScene(): void {
        this.onClose?.();
        this.scene.stop();
    }
}
